import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * What went wrong, as far as the member needs to know.
 *
 * The provider's ErrorCode arrives on the url as reasonId, but a raw code is
 * no use to the member - so each one is mapped to a cause and, more
 * importantly, to what to try next. Anything unrecognised falls back to
 * `unknown`, which advises a retry without guessing at a reason.
 */
interface FailureReason {
  /** Heading shown above the explanation. */
  title: string;
  /** What happened, in the member's terms. */
  message: string;
  /** Whether trying the same card again could plausibly work. */
  canRetry: boolean;
}

@Component({
  selector: 'fgb-unified-checkout-failure-page',
  templateUrl: './unified-checkout-failure-page.component.html',
  styleUrl: './unified-checkout-failure-page.component.scss',
})
export class UnifiedCheckoutFailurePageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  readonly reasonId = signal<string | null>(this.route.snapshot.queryParamMap.get('reasonId'));
  readonly orderId = signal<string | null>(this.route.snapshot.queryParamMap.get('orderId'));

  /**
   * Codes worth explaining individually, because the member's next step
   * genuinely differs. Everything else is a retry.
   *
   * Kept deliberately short: a long list of provider codes paraphrased into
   * prose goes stale the moment the provider adds one, and guessing wrongly at
   * a cause is worse than saying the payment did not go through.
   */
  private readonly reasons: Record<string, FailureReason> = {
    declined: {
      title: 'Your card was declined',
      message:
        'Your bank did not approve the payment. They will know why - it is often a routine security check rather than anything wrong with your account.',
      canRetry: false,
    },
    insufficientfunds: {
      title: 'Not enough available balance',
      message: 'Your bank turned the payment down for insufficient funds. Try another card, or top up and come back.',
      canRetry: false,
    },
    expiredcard: {
      title: 'That card has expired',
      message: 'The expiry date on the card has passed. Add a different card to complete your order.',
      canRetry: false,
    },
    cvvfailed: {
      title: 'Security code did not match',
      message: 'The security code did not match the card. Check the code on your card and enter it again.',
      canRetry: true,
    },
    processingerror: {
      title: 'The payment could not be processed',
      message: 'Something went wrong on the way to your bank. Nothing has been charged, so it is safe to try again.',
      canRetry: true,
    },
  };

  /** The fallback, used whenever the code is missing or unrecognised. */
  private readonly unknownReason: FailureReason = {
    title: "Your payment didn't go through",
    message:
      'We could not complete the payment, and your order has not been placed. Nothing has been charged, so you can safely try again.',
    canRetry: true,
  };

  /**
   * The failure to show. Matched case- and separator-insensitively, since the
   * provider's codes are not consistent about either.
   */
  readonly reason = computed<FailureReason>(() => {
    const code = this.reasonId();
    if (!code) {
      return this.unknownReason;
    }

    const key = code.toLowerCase().replace(/[^a-z]/g, '');
    return this.reasons[key] ?? this.unknownReason;
  });

  /**
   * Returns to the checkout, reusing the order where there is one.
   *
   * The order id is what makes this a retry rather than a fresh start: the
   * checkout page refetches that order instead of posting a new one, so the
   * basket and address the member already filled in are still there.
   */
  tryAgain(): void {
    const orderId = this.orderId();
    this.router.navigate(['/payment/checkout'], orderId ? { queryParams: { orderId } } : {});
  }
}
